import type { FileData, Part } from "@/types";
import type { BuildAdkUserPartsInput } from "./types";
import type { NormalizedAdkPart } from "./types";

const hasContent = (text?: string, files?: FileData[]) =>
  Boolean(text) || Boolean(files?.length);

const isSpecialPart = (part: Part) =>
  Boolean(
    part.functionCall ||
      part.functionResponse ||
      part.executableCode ||
      part.codeExecutionResult ||
      (part as any).actions,
  );

/**
 * 将后端 ADK parts 归一化成更适合 UI 渲染的片段。
 *
 * 设计目标：
 * - 连续 text 会被合并，减少流式分片直接渲染带来的碎片化。
 * - fileData 会合并到 fileDataList，支持“文件 + 文本”作为一条消息展示。
 * - functionCall、functionResponse、代码执行等特殊 part 保持独立，避免影响工具渲染。
 * - thought 发生切换时会结算当前缓冲，保证思考内容和普通回答不误合并。
 */
export const normalizeAdkParts = (parts: Part[] = []): NormalizedAdkPart[] => {
  const normalized: NormalizedAdkPart[] = [];
  let currentFiles: FileData[] = [];
  let currentText = "";
  let currentThought: boolean | undefined;

  parts.forEach((part) => {
    const thoughtChanged =
      part.thought !== currentThought && hasContent(currentText, currentFiles);

    if (isSpecialPart(part) || thoughtChanged) {
      if (hasContent(currentText, currentFiles)) {
        normalized.push({
          text: currentText || undefined,
          fileDataList: currentFiles.length ? currentFiles : undefined,
          thought: currentThought,
        });
        currentText = "";
        currentFiles = [];
      }
      if (typeof part.thought !== "undefined") {
        currentThought = part.thought;
      }
    }

    if (isSpecialPart(part)) {
      normalized.push({ ...part });
      return;
    }

    if (part.fileData) currentFiles.push(part.fileData as FileData);
    if (part.text) currentText += part.text;
    if (typeof part.thought !== "undefined") currentThought = part.thought;
  });

  if (hasContent(currentText, currentFiles)) {
    normalized.push({
      text: currentText || undefined,
      fileDataList: currentFiles.length ? currentFiles : undefined,
      thought: currentThought,
    });
  } else if (!normalized.length && parts.length > 0) {
    return parts.map((part) => ({ ...part }));
  }

  return normalized;
};

/**
 * 兼容早期只关注文本合并的调用方，语义上等同于 normalizeAdkParts。
 */
export const combineAdkParts = normalizeAdkParts;

/**
 * 将 SDK Sender 的输入内容转换为 ADK 请求体里的 user parts。
 *
 * 这里保持“附件 part 在前、文本 part 在后”的顺序，和业务项目 useDebug
 * 当前对后端协议的使用方式一致；functionResponse 场景只提交工具响应。
 */
export const buildAdkUserParts = ({
  text = "",
  files = [],
  functionResponse,
}: BuildAdkUserPartsInput): Part[] => {
  if (functionResponse) return [{ functionResponse }];

  const parts: Part[] = [];
  files.forEach((file) => {
    const fileUri =
      file.tempUrl ||
      file.fileUrl ||
      file.response?.fileUrl ||
      file.response?.tempUrl;
    if (!fileUri) return;

    parts.push({
      fileData: {
        fileUri,
        displayName:
          file.fileName || file.name || file.response?.fileName || "",
        mimeType: file.mimeType || file.response?.mimeType || "",
      },
    });
  });

  if (text.trim()) parts.push({ text });
  return parts;
};
